import styled, { css } from 'styled-components';

export const Container = styled.div`
  max-width: 64rem;
  margin: 3.2rem auto;
  padding: 0 1.6rem;
`;

export const Sheet = styled.div<{ $accent: string }>`
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1.6rem;
  box-shadow: 0 0.4rem 1.6rem rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

export const AccentStrip = styled.div<{ $accent: string }>`
  height: 0.5rem;
  background: ${({ $accent }) => $accent};
`;

export const SheetBody = styled.div`
  padding: 3.2rem;

  @media (max-width: 600px) {
    padding: 2rem 1.6rem;
  }
`;

/* ---------- Document header ---------- */

export const DocHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2.4rem;
  flex-wrap: wrap;
  margin-bottom: 2.4rem;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  min-width: 0;
`;

export const LogoBlock = styled.div<{ $accent: string }>`
  width: 6.4rem;
  height: 6.4rem;
  border-radius: 1.2rem;
  border: 0.2rem solid ${({ $accent }) => $accent};
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`;

export const LogoImage = styled.img<{ $cover?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $cover }) => ($cover ? 'cover' : 'contain')};
  padding: ${({ $cover }) => ($cover ? '0' : '0.4rem')};
`;

export const LogoFallback = styled.div<{ $accent: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $accent }) => `${$accent}1A`};
  color: ${({ $accent }) => $accent};
  font-size: 2rem;
  font-weight: 800;
`;

export const BusinessBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const BusinessName = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  line-height: 1.2;
`;

export const BusinessTagline = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-top: 0.2rem;
`;

export const HeaderRight = styled.div`
  text-align: right;
  min-width: 18rem;
`;

export const QuoteEyebrow = styled.div<{ $accent: string }>`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${({ $accent }) => $accent};
  letter-spacing: 0.2rem;
  line-height: 1;
`;

export const QuoteRef = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.slateLight};
  margin-top: 0.4rem;
  letter-spacing: 0.04rem;
`;

export const MetaTable = styled.div`
  margin-top: 1.2rem;
  display: grid;
  grid-template-columns: auto auto;
  gap: 0.4rem 1.2rem;
  justify-content: flex-end;
`;

export const MetaLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.06rem;
  text-align: right;
`;

export const MetaValue = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.darkSlate};
  text-align: right;
`;

export const ValidPill = styled.span<{ $accent: string }>`
  display: inline-flex;
  padding: 0.2rem 0.8rem;
  border-radius: 999rem;
  background: ${({ $accent }) => `${$accent}1F`};
  color: ${({ $accent }) => $accent};
  border: 0.1rem solid ${({ $accent }) => `${$accent}52`};
  font-size: 1.2rem;
  font-weight: 700;
`;

/* ---------- Parties (From / Bill to) ---------- */

export const Parties = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.4rem;
  margin-top: 1.6rem;
  padding: 2rem;
  background: ${({ theme }) => theme.colors.surfaceSoft};
  border-radius: 1.2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const PartyBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

export const PartyLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  margin-bottom: 0.4rem;
`;

export const PartyName = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const PartyLine = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.slate};
  word-break: break-word;
`;

/* ---------- Quote title ---------- */

export const QuoteTitleSection = styled.div`
  margin-top: 2.4rem;
  font-size: 1.7rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

/* ---------- Banners ---------- */

const bannerBase = css`
  border-radius: 0.8rem;
  padding: 1rem 1.2rem;
  margin: 1.6rem 0 0;
  font-size: 1.3rem;
  border-left: 0.4rem solid;
`;

export const Banner = styled.div`
  ${bannerBase};
  background: ${({ theme }) => theme.colors.amberBgSoft};
  border-left-color: ${({ theme }) => theme.colors.amber};
  color: ${({ theme }) => theme.colors.amberText};
`;

/* ---------- Line items table ---------- */

export const LineItemsTable = styled.table<{ $accent: string }>`
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0 0.8rem;

  thead tr {
    border-top: 0.2rem solid ${({ $accent }) => $accent};
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.border};
  }

  th {
    text-align: left;
    font-size: 1.1rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.slateGrey};
    text-transform: uppercase;
    letter-spacing: 0.06rem;
    padding: 0.8rem 0;
  }

  td {
    padding: 1rem 0;
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.darkSlate};
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.border};
  }

  th:nth-child(2),
  td:nth-child(2) {
    text-align: right;
    color: ${({ theme }) => theme.colors.slateGrey};
    width: 6rem;
  }

  th:nth-child(3),
  td:nth-child(3) {
    text-align: right;
    font-weight: 700;
    width: 12rem;
    font-feature-settings: 'tnum';
  }
`;

/* ---------- Detail row + dates ---------- */

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
`;

export const DetailLabel = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.04rem;
`;

export const DetailValue = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  font-weight: 600;
`;

/* ---------- Total card ---------- */

export const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.6rem;
`;

export const TotalCard = styled.div<{ $accent: string }>`
  min-width: 26rem;
  background: ${({ $accent }) => `${$accent}14`};
  border: 0.1rem solid ${({ $accent }) => $accent};
  border-radius: 1rem;
  padding: 1.4rem 1.8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.6rem;
`;

export const TotalLabel = styled.span<{ $accent: string }>`
  font-size: 1.2rem;
  font-weight: 800;
  color: ${({ $accent }) => $accent};
  text-transform: uppercase;
  letter-spacing: 0.08rem;
`;

export const TotalValue = styled.strong<{ $accent: string }>`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${({ $accent }) => $accent};
  font-feature-settings: 'tnum';
`;

/* ---------- Notes ---------- */

export const NotesSection = styled.div`
  margin-top: 2rem;
  padding: 1.6rem;
  background: ${({ theme }) => theme.colors.surfaceSoft};
  border-radius: 1rem;
`;

export const NotesLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  margin-bottom: 0.6rem;
`;

export const NotesBody = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.slate};
  line-height: 1.6;
  white-space: pre-wrap;
`;

/* ---------- Actions ---------- */

export const Actions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2.4rem;
`;

export const PrimaryBtn = styled.button<{ $accent: string }>`
  flex: 1;
  background: ${({ $accent }) => $accent};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  font-size: 1.5rem;
  font-weight: 700;
  padding: 1.4rem 1.6rem;
  border-radius: 1rem;
  cursor: pointer;
  text-decoration: none;
  text-align: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SecondaryBtn = styled.button`
  flex: 1;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.darkSlate};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  font-size: 1.4rem;
  font-weight: 700;
  padding: 1.4rem 1.6rem;
  border-radius: 1rem;
  cursor: pointer;
`;

/* ---------- Decline form ---------- */

export const DeclineForm = styled.div`
  margin-top: 1.6rem;
`;

export const DeclineLabel = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-bottom: 0.6rem;
`;

export const DeclineInput = styled.textarea`
  width: 100%;
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 0.8rem;
  padding: 1rem;
  font-size: 1.4rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.darkSlate};
  resize: vertical;
  margin-bottom: 1rem;
`;

/* ---------- Footer ---------- */

export const Footer = styled.div`
  margin-top: 2.4rem;
  padding-top: 1.6rem;
  border-top: 0.1rem solid ${({ theme }) => theme.colors.border};
  text-align: center;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  line-height: 1.6;
`;

/* ---------- Empty / loading ---------- */

export const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1.6rem;
  padding: 2.4rem;
`;

export const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin: 0 0 0.8rem;
`;

export const Body = styled.p`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.slate};
  line-height: 1.6;
  margin: 0 0 1.6rem;
`;
